import {
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Stepper,
  Text,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import {
  useGetProjectsAutodeskAvailableProjectsQuery,
  usePostProjectsAutodeskLoginStartMutation,
  usePutProjectsAutodeskConnectMutation,
} from "../../../../../openapi";

interface IConnectAutodeskProjectModalProps {
  opened: boolean;
  onClose: () => void;
  onConnected: () => void;
  projectId?: string;
}

export const ConnectAutodeskProjectModal = ({
  opened,
  onClose,
  onConnected,
  projectId,
}: IConnectAutodeskProjectModalProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [autodeskLoginError, setAutodeskLoginError] = useState<string | null>(null);
  const [selectedHubId, setSelectedHubId] = useState<string>("all");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const [startAutodeskLogin, { isLoading: isStartingAutodeskLogin, isError: startAutodeskLoginFailed }] =
    usePostProjectsAutodeskLoginStartMutation();
  const [connectAutodeskProject, { isLoading: isConnectingAutodeskProject, isError: connectAutodeskProjectFailed }] =
    usePutProjectsAutodeskConnectMutation();
  const {
    data: availableProjectsData,
    isFetching: isFetchingAvailableProjects,
    isError: availableProjectsFailed,
  } = useGetProjectsAutodeskAvailableProjectsQuery(undefined, {
    skip: activeStep < 1,
  });

  const availableProjects = availableProjectsData?.projects ?? [];
  const availableHubs = availableProjectsData?.hubs ?? [];
  const filteredProjects =
    selectedHubId === "all"
      ? availableProjects
      : availableProjects.filter((availableProject) => availableProject.hubId === selectedHubId);
  const selectedProject = availableProjects.find(
    (availableProject) => availableProject.projectId === selectedProjectId,
  );
  const projectCountText =
    selectedHubId === "all"
      ? `${availableProjects.length} tilgjengelige prosjekter`
      : `${filteredProjects.length} tilgjengelige prosjekter i denne hubben`;

  useEffect(() => {
    const handleAutodeskOAuthMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data?.type !== "mcapps:autodesk-oauth") {
        return;
      }

      popupRef.current?.close();
      popupRef.current = null;

      if (event.data.status === "success") {
        replaceAutodeskLoginStatusInUrl("success");
        setAutodeskLoginError(null);
        setActiveStep(1);
        return;
      }

      replaceAutodeskLoginStatusInUrl(event.data.status ?? "failed");
      setAutodeskLoginError("Autodesk-innloggingen ble ikke fullført. Prøv igjen.");
    };

    window.addEventListener("message", handleAutodeskOAuthMessage);
    return () => window.removeEventListener("message", handleAutodeskOAuthMessage);
  }, []);

  const handleStartAutodeskLogin = async () => {
    if (projectId == null) {
      return;
    }

    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    const response = await startAutodeskLogin({
      body: {
        projectId,
        returnPath,
        openerOrigin: window.location.origin,
        popup: true,
      },
    }).unwrap();

    const popup = window.open(
      response.authorizationUrl,
      "mcapps-autodesk-login",
      "popup=yes,width=680,height=760,left=200,top=80",
    );

    if (popup == null) {
      window.location.assign(response.authorizationUrl);
      return;
    }

    popupRef.current = popup;
    popup.focus();
  };

  const handleConnectAutodeskProject = async () => {
    if (projectId == null || selectedProject == null) {
      return;
    }

    await connectAutodeskProject({
      body: {
        projectId,
        accountId: selectedProject.accountId,
        hubId: selectedProject.hubId,
        autodeskProjectId: selectedProject.projectId,
        name: selectedProject.projectName,
        region: toAutodeskRegion(selectedProject.region),
        folderUrn: selectedProject.rootFolderUrn,
        readSubfolders: false,
      },
    }).unwrap();

    onConnected();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Koble Autodesk Forma-prosjekt" size="xl">
      <Stack gap="md">
        <Stepper active={activeStep} onStepClick={setActiveStep}>
          <Stepper.Step label="Logg inn" description="Autodesk">
            <Stack gap="md" mt="md">
              <Text>
                Logg inn med Autodesk-brukeren din. MCApps bruker innloggingen til å finne Forma-prosjektene du allerede
                har tilgang til.
              </Text>

              <Alert color="blue" title="Hva skjer etter innlogging?">
                Du blir sendt til Autodesk for innlogging. Når du kommer tilbake, kan du velge prosjektet som skal
                kobles til MCApps.
              </Alert>

              {startAutodeskLoginFailed && (
                <Alert color="red">Kunne ikke starte Autodesk-innloggingen. Prøv igjen om litt.</Alert>
              )}
              {autodeskLoginError != null && <Alert color="red">{autodeskLoginError}</Alert>}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Velg prosjekt" description="Prosjekt/mappe">
            <Stack gap="md" mt="md">
              <Text>
                Velg Forma-prosjektet og mappen MCApps skal lese filer fra. Du ser bare prosjekter og mapper Autodesk-
                brukeren din har tilgang til.
              </Text>

              {isFetchingAvailableProjects && <Loader size="sm" />}

              {availableProjectsFailed && <Alert color="red">Kunne ikke hente Forma-prosjektene fra Autodesk.</Alert>}

              {!isFetchingAvailableProjects && !availableProjectsFailed && (
                <Stack gap="sm">
                  {availableHubs.length > 1 && (
                    <SegmentedControl
                      color="blue"
                      size="xs"
                      value={selectedHubId}
                      onChange={(value) => {
                        setSelectedHubId(value);
                        setSelectedProjectId(null);
                      }}
                      data={[
                        { value: "all", label: "Alle" },
                        ...availableHubs.map((hub) => ({
                          value: hub.hubId,
                          label: hub.hubName,
                        })),
                      ]}
                    />
                  )}
                  <Text c="dimmed" size="sm">
                    {projectCountText}
                  </Text>

                  <Select
                    searchable
                    clearable
                    label="Forma-prosjekt"
                    placeholder="Velg prosjekt"
                    data={filteredProjects.map((availableProject) => ({
                      value: availableProject.projectId,
                      label: formatAutodeskProjectOptionLabel(availableProject),
                    }))}
                    value={selectedProjectId}
                    onChange={setSelectedProjectId}
                  />
                </Stack>
              )}

              {selectedProject != null && (
                <Alert color="blue" title="Mappe">
                  MCApps kobles foreløpig til prosjektets rotmappe. Mappene kan avgrenses mer presist i neste iterasjon.
                </Alert>
              )}

              {connectAutodeskProjectFailed && <Alert color="red">Kunne ikke koble Forma-prosjektet til MCApps.</Alert>}
            </Stack>
          </Stepper.Step>

        </Stepper>

        <Divider />

        <Group justify="space-between">
          <Button variant="default" onClick={onClose}>
            Avbryt
          </Button>
          <Group>
            {activeStep > 0 && (
              <Button variant="default" onClick={() => setActiveStep((currentStep) => currentStep - 1)}>
                Tilbake
              </Button>
            )}
            {activeStep === 0 && (
              <Button
                color="blue"
                disabled={projectId == null}
                loading={isStartingAutodeskLogin}
                onClick={handleStartAutodeskLogin}
              >
                Logg inn med Autodesk
              </Button>
            )}
            {activeStep === 1 && (
              <Button
                variant="primary"
                disabled={selectedProject == null || projectId == null}
                loading={isConnectingAutodeskProject}
                onClick={handleConnectAutodeskProject}
              >
                Anslut konto
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

const replaceAutodeskLoginStatusInUrl = (status: string) => {
  const hash = window.location.hash;

  if (hash.startsWith("#/")) {
    const hashWithoutPrefix = hash.slice(1);
    const [hashPathAndSearch, hashFragment] = hashWithoutPrefix.split("#", 2);
    const [hashPath, hashSearch = ""] = hashPathAndSearch.split("?", 2);
    const hashSearchParams = new URLSearchParams(hashSearch);
    hashSearchParams.set("autodeskLogin", status);

    const nextHash = `#${hashPath}?${hashSearchParams.toString()}${hashFragment ? `#${hashFragment}` : ""}`;
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
    return;
  }

  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set("autodeskLogin", status);
  window.history.replaceState(null, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
};

const toAutodeskRegion = (region: string | null): "US" | "EMEA" => {
  return region === "US" ? "US" : "EMEA";
};

const formatAutodeskProjectOptionLabel = (project: {
  hubName: string;
  projectName: string;
  projectNumber: string | null;
}) => {
  const projectNumber = project.projectNumber != null ? `${project.projectNumber} - ` : "";
  return `${projectNumber}${project.projectName} (${project.hubName})`;
};
